/**
 * CSV Parsing Service
 * 
 * Provides functionality to parse CSV and Excel files (xlsx, xls, csv)
 * containing shipment data.
 * 
 * @module CSVParsingService
 */

import * as XLSX from 'xlsx';
import { ShipmentRow, ParseError } from '../@types/shipment';

export interface ParseResult {
  shipments: ShipmentRow[];
  errors: ParseError[];
  metadata: {
    totalRows: number;
    validRows: number;
    invalidRows: number;
  };
}

// Supported file extensions
export const SUPPORTED_FILE_EXTENSIONS = ['.csv', '.xlsx', '.xls', '.xlsm', '.xlsb'];
export const SUPPORTED_FILE_TYPES = [
  'text/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel.sheet.macroEnabled.12',
  'application/vnd.ms-excel.sheet.binary.macroEnabled.12'
];

/**
 * CSV/Excel Parsing Service
 * Handles parsing of shipment CSV and Excel files
 */
export class CSVParsingService {
  /**
   * Detect CSV delimiter (comma or semicolon)
   * 
   * @param firstLine - First line of the CSV file (header row)
   * @returns Detected delimiter character
   */
  private static detectDelimiter(firstLine: string): string {
    // Count occurrences of comma and semicolon
    const commaCount = (firstLine.match(/,/g) || []).length;
    const semicolonCount = (firstLine.match(/;/g) || []).length;
    
    // Use the delimiter that appears more frequently
    // If both are equal or both are 0, default to comma
    if (semicolonCount > commaCount) {
      console.log('[CSVParsingService] Detected semicolon delimiter');
      return ';';
    } else {
      console.log('[CSVParsingService] Detected comma delimiter');
      return ',';
    }
  }

  /**
   * Check if a file is a supported format
   */
  static isSupportedFile(file: File): boolean {
    const fileName = file.name.toLowerCase();
    return SUPPORTED_FILE_EXTENSIONS.some(ext => fileName.endsWith(ext));
  }

  /**
   * Get file extension
   */
  private static getFileExtension(fileName: string): string {
    const lastDot = fileName.lastIndexOf('.');
    return lastDot !== -1 ? fileName.substring(lastDot).toLowerCase() : '';
  }

  /**
   * Parse a shipment file (CSV or Excel)
   * 
   * @param file - CSV or Excel file to parse
   * @returns Promise resolving to parse result with shipments and errors
   */
  static async parseShipmentFile(file: File): Promise<ParseResult> {
    const extension = this.getFileExtension(file.name);
    
    console.log('[CSVParsingService] Starting file parse:', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      extension
    });

    // Route to appropriate parser based on file extension
    if (extension === '.csv') {
      return this.parseCSVFile(file);
    } else if (['.xlsx', '.xls', '.xlsm', '.xlsb'].includes(extension)) {
      return this.parseExcelFile(file);
    } else {
      // Try to detect based on content
      console.log('[CSVParsingService] Unknown extension, attempting to detect format...');
      try {
        return this.parseExcelFile(file);
      } catch {
        return this.parseCSVFile(file);
      }
    }
  }

  /**
   * Parse an Excel file (xlsx, xls, xlsm, xlsb)
   */
  private static async parseExcelFile(file: File): Promise<ParseResult> {
    const errors: ParseError[] = [];
    const shipments: ShipmentRow[] = [];

    try {
      console.log('[CSVParsingService] Parsing Excel file:', file.name);

      // Read file as array buffer
      const arrayBuffer = await this.readFileAsArrayBuffer(file);
      
      // Parse with xlsx library
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      
      // Get first sheet
      const sheetName = workbook.SheetNames[0];
      if (!sheetName) {
        throw new Error('No sheets found in workbook');
      }
      
      const worksheet = workbook.Sheets[sheetName];
      console.log('[CSVParsingService] Parsing sheet:', sheetName);
      
      // Convert to JSON array (header row becomes keys)
      const jsonData = XLSX.utils.sheet_to_json<Record<string, any>>(worksheet, { 
        defval: '',
        raw: false // Convert all values to strings
      });
      
      if (jsonData.length === 0) {
        throw new Error('No data found in sheet');
      }

      // Get headers from first row
      const headers = Object.keys(jsonData[0]);
      console.log('[CSVParsingService] Headers found:', headers);

      // Validate required headers
      const requiredHeaders = ['TrackingID', 'ShipZone', 'CustomerNo', 'InvoiceNo', 'SalesOrderNo'];
      const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
      
      if (missingHeaders.length > 0) {
        errors.push({
          row: 1,
          message: `Missing required headers: ${missingHeaders.join(', ')}`,
          field: 'headers'
        });
      }

      // Process each row
      jsonData.forEach((row, index) => {
        const rowNumber = index + 2; // +2 for 1-indexed + header row
        
        try {
          // Create shipment object with trimmed values
          const shipment: any = {};
          Object.entries(row).forEach(([key, value]) => {
            shipment[key.trim()] = String(value || '').trim();
          });

          // Validate required fields
          const validationErrors = this.validateShipmentRow(shipment, rowNumber);
          if (validationErrors.length > 0) {
            errors.push(...validationErrors);
            return;
          }

          shipments.push(shipment as ShipmentRow);
        } catch (error: any) {
          errors.push({
            row: rowNumber,
            message: `Error parsing row: ${error.message}`,
            data: JSON.stringify(row)
          });
        }
      });

      console.log('[CSVParsingService] Excel parse complete:', {
        totalRows: jsonData.length,
        validRows: shipments.length,
        invalidRows: errors.length
      });

      return {
        shipments,
        errors,
        metadata: {
          totalRows: jsonData.length,
          validRows: shipments.length,
          invalidRows: errors.length
        }
      };
    } catch (error: any) {
      console.error('[CSVParsingService] Fatal error parsing Excel file:', error);
      errors.push({
        row: 0,
        message: `Fatal error: ${error.message}`,
        field: 'file'
      });

      return {
        shipments: [],
        errors,
        metadata: {
          totalRows: 0,
          validRows: 0,
          invalidRows: errors.length
        }
      };
    }
  }

  /**
   * Read file as ArrayBuffer for Excel parsing
   */
  private static async readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const result = e.target?.result;
        if (result instanceof ArrayBuffer) {
          resolve(result);
        } else {
          reject(new Error('Failed to read file as ArrayBuffer'));
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Error reading file'));
      };
      
      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * Parse a CSV file
   * 
   * @param file - CSV file to parse
   * @returns Promise resolving to parse result with shipments and errors
   */
  private static async parseCSVFile(file: File): Promise<ParseResult> {
    const errors: ParseError[] = [];
    const shipments: ShipmentRow[] = [];

    try {
      console.log('[CSVParsingService] Parsing CSV file:', file.name);

      // Read file content
      const text = await this.readFileAsText(file);
      
      // Split into lines
      const lines = text.split(/\r?\n/).filter(line => line.trim().length > 0);
      
      if (lines.length < 2) {
        throw new Error('File must contain at least a header row and one data row');
      }

      // Detect delimiter from first line
      const delimiter = this.detectDelimiter(lines[0]);

      // Parse header row
      const headers = this.parseCSVLine(lines[0], delimiter);
      console.log('[CSVParsingService] Headers found:', headers.length);

      // Validate required headers
      const requiredHeaders = ['TrackingID', 'ShipZone', 'CustomerNo', 'InvoiceNo', 'SalesOrderNo'];
      const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
      
      if (missingHeaders.length > 0) {
        errors.push({
          row: 1,
          message: `Missing required headers: ${missingHeaders.join(', ')}`,
          field: 'headers'
        });
      }

      // Parse data rows
      for (let i = 1; i < lines.length; i++) {
        const lineNumber = i + 1; // 1-indexed for user display
        const line = lines[i];

        try {
          // Skip empty lines
          if (!line.trim()) {
            continue;
          }

          const values = this.parseCSVLine(line, delimiter);
          
          // Create shipment row object
          const shipment: any = {};
          
          headers.forEach((header, index) => {
            const value = values[index] || '';
            // Normalize header names (remove spaces, handle special characters)
            const normalizedHeader = header.trim();
            shipment[normalizedHeader] = value.trim();
          });

          // Validate required fields
          const validationErrors = this.validateShipmentRow(shipment, lineNumber);
          if (validationErrors.length > 0) {
            errors.push(...validationErrors);
            continue;
          }

          shipments.push(shipment as ShipmentRow);
        } catch (error: any) {
          errors.push({
            row: lineNumber,
            message: `Error parsing row: ${error.message}`,
            data: line
          });
        }
      }

      console.log('[CSVParsingService] CSV parse complete:', {
        totalRows: lines.length - 1,
        validRows: shipments.length,
        invalidRows: errors.length
      });

      return {
        shipments,
        errors,
        metadata: {
          totalRows: lines.length - 1,
          validRows: shipments.length,
          invalidRows: errors.length
        }
      };
    } catch (error: any) {
      console.error('[CSVParsingService] Fatal error parsing CSV file:', error);
      errors.push({
        row: 0,
        message: `Fatal error: ${error.message}`,
        field: 'file'
      });

      return {
        shipments: [],
        errors,
        metadata: {
          totalRows: 0,
          validRows: 0,
          invalidRows: errors.length
        }
      };
    }
  }

  /**
   * Read file as text with encoding detection
   * 
   * @param file - File to read
   * @returns Promise resolving to file text content
   */
  private static async readFileAsText(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const result = e.target?.result;
        if (typeof result === 'string') {
          resolve(result);
        } else {
          reject(new Error('Failed to read file as text'));
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Error reading file'));
      };
      
      // Try to read with UTF-8 encoding first
      reader.readAsText(file, 'UTF-8');
    });
  }

  /**
   * Parse a CSV line (comma or semicolon-delimited)
   * Handles quoted values and escaped quotes
   * 
   * @param line - CSV line to parse
   * @param delimiter - Delimiter character (',' or ';')
   * @returns Array of field values
   */
  private static parseCSVLine(line: string, delimiter: string = ','): string[] {
    const values: string[] = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];
      
      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          // Escaped quote
          current += '"';
          i++; // Skip next quote
        } else {
          // Toggle quote state
          inQuotes = !inQuotes;
        }
      } else if (char === delimiter && !inQuotes) {
        // Field separator
        values.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    
    // Add last field
    values.push(current);
    
    return values;
  }

  /**
   * Validate a shipment row
   * 
   * @param shipment - Shipment row to validate
   * @param rowNumber - Row number for error reporting
   * @returns Array of validation errors
   */
  private static validateShipmentRow(shipment: any, rowNumber: number): ParseError[] {
    const errors: ParseError[] = [];

    // Check required fields
    if (!shipment.TrackingID || !shipment.TrackingID.trim()) {
      errors.push({
        row: rowNumber,
        field: 'TrackingID',
        message: 'TrackingID is required'
      });
    }

    if (!shipment.ShipZone || !shipment.ShipZone.trim()) {
      errors.push({
        row: rowNumber,
        field: 'ShipZone',
        message: 'ShipZone is required'
      });
    }

    if (!shipment.CustomerNo || !shipment.CustomerNo.trim()) {
      errors.push({
        row: rowNumber,
        field: 'CustomerNo',
        message: 'CustomerNo is required'
      });
    }

    if (!shipment.InvoiceNo || !shipment.InvoiceNo.trim()) {
      errors.push({
        row: rowNumber,
        field: 'InvoiceNo',
        message: 'InvoiceNo is required'
      });
    }

    if (!shipment.SalesOrderNo || !shipment.SalesOrderNo.trim()) {
      errors.push({
        row: rowNumber,
        field: 'SalesOrderNo',
        message: 'SalesOrderNo is required'
      });
    }

    // Validate date formats if present
    if (shipment.ShipDate && !this.isValidDate(shipment.ShipDate)) {
      errors.push({
        row: rowNumber,
        field: 'ShipDate',
        message: `Invalid date format: ${shipment.ShipDate}`
      });
    }

    // Validate numeric fields
    if (shipment.QuantityShipped && isNaN(Number(shipment.QuantityShipped))) {
      errors.push({
        row: rowNumber,
        field: 'QuantityShipped',
        message: `Invalid number: ${shipment.QuantityShipped}`
      });
    }

    return errors;
  }

  /**
   * Check if a string is a valid date
   * 
   * @param dateString - Date string to validate
   * @returns True if valid date
   */
  private static isValidDate(dateString: string): boolean {
    // Try various date formats
    const formats = [
      /^\d{1,2}\/\d{1,2}\/\d{4}$/, // DD/MM/YYYY or MM/DD/YYYY
      /^\d{4}-\d{2}-\d{2}$/, // YYYY-MM-DD
      /^\d{2}\/\d{2}\/\d{4}$/ // DD/MM/YYYY
    ];

    return formats.some(format => format.test(dateString));
  }
}

